import {
  Tile,
  TileContent,
  TileGroup,
  TileText,
} from '@one-thd/sui-atomic-components';
import {
  params,
  shape,
  string as stringType,
  number as numberType,
} from '@thd-nucleus/data-sources';
import { GridView, ListView } from '@one-thd/sui-icons';
import classNames from 'classnames';
import React, { ChangeEvent, useEffect } from 'react';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { useLoyaltyMembershipDetails } from '../../../hooks/useDataSources';
import { pushEventInfo } from '../../../utils/analytics';
import { getWindow } from '../../../utils/general';

const initialTileData = [
  {
    value: 'Card',
    text: 'Card View',
    icon: <GridView color="current" size="small" />,
  },
  {
    value: 'Table',
    text: 'Table View',
    icon: <ListView color="current" size="small" />,
  },
];

export const OrderViewToggle = () => {
  const { viewType, setViewType } = usePurchaseHistoryContext();
  const { data, loading } = useLoyaltyMembershipDetails() ?? {};
  const [tileData, setTileData] = React.useState(initialTileData);

  const containsLocalView = tileData.some((tile) => tile.value === 'localView');

  /**
   * Logic checks the proTiers benefitTags to see if the account is a managed account
   * Where tag.tagName includes the text 'AMBenefit' && tag.value === 'Confirmed'
   */
  const managedAccountCheck = (proTiers: {
    benefitTags: { tagName: string; value: string; __typename: string }[];
  }): boolean => {
    if (proTiers === undefined) return false;
    return proTiers?.benefitTags.some(
      (tag) => tag.tagName.includes('AMBenefit') && tag.value === 'Confirmed',
    );
  };

  useEffect(() => {
    if (!loading && data) {
      const isManagedAccount: boolean = managedAccountCheck(
        data?.loyaltyMembershipDetails?.proTiers,
      );

      if (isManagedAccount && !containsLocalView) {
        setTileData((prevTileData) => [
          ...prevTileData,
          {
            value: 'LocalDeliveries',
            text: 'Local Deliveries View',
            icon: <ListView color="current" size="small" />,
          },
        ]);
      }
    }
  }, [data?.loyaltyMembershipDetails?.proTiers]);

  return (
    <div className="sui-mr-3" data-testid="orderViewToggle">
      <TileGroup
        onChange={(
          _event: ChangeEvent<HTMLInputElement>,
          newViewType: string,
        ) => {
          if (newViewType) {
            setViewType(newViewType);

            // LocalDeliveries should not be set in localStorage
            if (newViewType !== 'LocalDeliveries') {
              getWindow()?.localStorage.setItem('viewType', newViewType);
            }

            pushEventInfo({
              category: { primaryCategory: 'button submission' },
              eventInfo: { eventName: 'my account settings' },
              myAccount: {
                settings: `purchase history: toggle: ${(() => {
                  if (newViewType === 'Card') {
                    return 'card';
                  }
                  if (newViewType === 'Table') {
                    return 'list';
                  }
                  if (newViewType === 'LocalDeliveries') {
                    return 'local deliveries';
                  }
                  return '';
                })()} view`,
              },
            });
          }
        }}
        value={viewType}
        aria-label="View Options"
      >
        {tileData.map((item, key) => (
          <Tile key={key} value={item.value} aria-label={item.text}>
            <TileContent>
              <span
                className={classNames('sui-pt-1', {
                  'sui-text-brand': viewType === item.value,
                })}
              >
                {item.icon}
              </span>
              <TileText>{item.text}</TileText>
            </TileContent>
          </Tile>
        ))}
      </TileGroup>
    </div>
  );
};

const proTiers = shape({
  benefitTags: shape({
    tagName: stringType(),
    value: stringType(),
  }),
});

OrderViewToggle.dataModel = {
  loyaltyMembershipDetails: params({
    channelId: numberType().isRequired(),
    customerAccountId: stringType().isRequired(),
    userId: stringType().isRequired(),
  }).shape({
    proTiers,
  }),
};
